import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CODE from '../../../../../Static/Constants/StatusCodes';
import { getSohHistory, sohHistoryAction } from '../../../../../Api/SpecificBatteryApi';
import { usePagesContent } from '../../../../../Hooks/usePagesContent';
import { useToast } from '../../../../../Hooks/useToast';
import MonthlyHistoricalPivotTable from '../../../../../Features/Table/MonthlyHistoricalPivotTable';
import NameTheCSV from '../../../../../Helper/CSVOperations/NameTheCSV';
import { getAvailableEmailDomains } from '../../../../../Helper/Organization/EmailDomains';
import { getCurrentTimestamp } from '../../../../../Helper/DatePicker/DateConverter';
import Events from '../../../../../Analytics/Events';
import {
    ALL_FILTER,
    ANY_CONFIDENCE,
    buildEntityOptions,
    buildEntitySlots,
    buildSohHistoryCsvRows,
    buildSohHistoryMailBody,
    buildSohHistoryParams,
    buildSohHistoryView,
    buildSohPivotModel,
    buildStatusFilterOptions,
    canRunSohHistoryActions,
    getEntityFilterLevel,
    getMonthRangeSummary,
    getSohHistoryWindow,
    resolveStatusFrom,
    shouldShowEntityFilter,
} from '../../../../../Helper/BatteryHealth/SohHistoryHelper';
import HeaderIdentifierBox, { resolveHeaderIdentifier } from '../HeaderIdentifierBox';
import {
    useBatteryInfo,
    useBatteryStructure,
    useBatterySystem,
    useVehicleInfo,
} from '../../../../../Pages/DigitalTwinV3/DigitalTwinContext';
import { buildSohHistoryFilters } from './SohHistoryFilterBar';
import SohPivotCell from './SohPivotCell';

const INITIAL_FILTERS = { status: ALL_FILTER, entity: ALL_FILTER, confidence: ANY_CONFIDENCE };
const CSV_COMPONENT_NAME = 'SOH Monthly History';
const EVENT_PREFIX = 'Digital Twin Battery Health SOH Monthly history';
const EMPTY_DATA = { history: [], forecast: [] };

/**
 * "Monthly history" view (table type "monthlyHistoricalPivot") of the Battery
 * Health "State of Health" section. The API returns raw sohML / rulForecastML
 * rows for the window; months, entity slots (from the Digital Twin battery
 * tree), filters, counts, the pivot and the CSV are all built here.
 * Filters never refetch: only a new battery or window does.
 * monthlyConfig:         resolved config from resolveMonthlyHistoryConfig
 * identifierFallbackRow: first Latest snapshot row, for the identifier box fallback
 */
const SohMonthlyHistory = ({ batteryID, monthlyConfig, identifierFallbackRow }) => {
    const { t } = useTranslation();
    // Org-meta labels: translated with the separators off (a ":" must not be
    // read as a namespace). Backend text (reasons) is not translated.
    const tLabel = useCallback((text) => t(text, { nsSeparator: false, keySeparator: false }), [t]);
    const { batteryInfo } = useBatteryInfo();
    const vehicleInfo = useVehicleInfo();
    const batterySystem = useBatterySystem();
    const batteryStructure = useBatteryStructure();
    const { openNotification } = useToast();
    const pageContent = usePagesContent();

    const [filters, setFilters] = useState(INITIAL_FILTERS);
    const [data, setData] = useState(EMPTY_DATA);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // A different battery starts with fresh filters. Reset during render (not
    // in an effect) so the fetch effect only ever sees the new battery.
    const [stateBatteryID, setStateBatteryID] = useState(batteryID);
    if (stateBatteryID !== batteryID) {
        setStateBatteryID(batteryID);
        setFilters(INITIAL_FILTERS);
    }

    // Window from the current month in the user's timezone, fixed for the mount.
    const [currentMonth] = useState(() => getCurrentTimestamp('YYYY-MM'));
    const historyWindow = useMemo(
        () => getSohHistoryWindow({
            currentMonth,
            pastMonths: monthlyConfig.pastMonths,
            futureMonths: monthlyConfig.futureMonths,
        }),
        [currentMonth, monthlyConfig.pastMonths, monthlyConfig.futureMonths]
    );
    const statusLabels = monthlyConfig.statusLabels;
    const emailDomains = useMemo(() => getAvailableEmailDomains(pageContent?.metaInfo), [pageContent]);

    // Keyed by value: only a new battery or window refetches.
    const paramsKey = JSON.stringify(buildSohHistoryParams({ batteryID, window: historyWindow }));

    useEffect(() => {
        const requestParams = JSON.parse(paramsKey);
        if (!requestParams.batteryID || !requestParams.startDate) {
            setData(EMPTY_DATA);
            setIsLoading(false);
            return undefined;
        }
        let active = true;
        // Cancel the superseded request on the network too, not just ignore it.
        const controller = new AbortController();
        setIsLoading(true);
        setHasError(false);
        (async () => {
            try {
                const res = await getSohHistory(requestParams, controller.signal);
                if (!active) return;
                const code = res?.responseStatus?.code;
                if (code === CODE.SUCCESS) {
                    const payload = res?.response?.response || {};
                    setData({
                        history: Array.isArray(payload.history) ? payload.history : [],
                        forecast: Array.isArray(payload.forecast) ? payload.forecast : [],
                    });
                } else if (code === CODE.NODATA) {
                    setData(EMPTY_DATA);
                } else {
                    setData(EMPTY_DATA);
                    setHasError(true);
                }
            } catch (err) {
                if (!active || controller.signal.aborted) return;
                console.error('SOH monthly history fetch error:', err);
                setData(EMPTY_DATA);
                setHasError(true);
            } finally {
                if (active) setIsLoading(false);
            }
        })();
        return () => {
            active = false;
            controller.abort();
        };
    }, [paramsKey]);

    // Slots of the shown levels plus the statusFrom level (month entity).
    const slots = useMemo(() => {
        const levels = Array.from(new Set([...(monthlyConfig.levels || []), monthlyConfig.statusFrom].filter(Boolean)));
        return buildEntitySlots({
            batterySystem,
            batteryStructure,
            levels,
            rows: [...data.history, ...data.forecast],
            // Same translated labels as the sidebar; used by rows, dropdown and CSV.
            t,
        });
    }, [batterySystem, batteryStructure, monthlyConfig.levels, monthlyConfig.statusFrom, data, t]);
    // statusFrom, unless its level has no slot and no row (then the first level that has).
    const statusFrom = useMemo(
        () => resolveStatusFrom({
            statusFrom: monthlyConfig.statusFrom,
            levels: monthlyConfig.levels,
            slots,
            rows: [...data.history, ...data.forecast],
        }),
        [monthlyConfig.statusFrom, monthlyConfig.levels, slots, data]
    );
    const shownSlots = useMemo(
        () => slots.filter((slot) => (monthlyConfig.levels || []).includes(slot.entityType)),
        [slots, monthlyConfig.levels]
    );

    const view = useMemo(
        () => buildSohHistoryView({
            history: data.history,
            forecast: data.forecast,
            slots,
            window: historyWindow,
            statusFrom,
            levels: monthlyConfig.levels,
            filters,
        }),
        [data, slots, historyWindow, statusFrom, monthlyConfig.levels, filters]
    );

    // Row labels come from org meta and entity labels from the slots: shown as is.
    const model = useMemo(
        () => buildSohPivotModel({ view, rowsConfig: monthlyConfig.rows, statusLabels }),
        [view, monthlyConfig.rows, statusLabels]
    );

    const statusOptions = useMemo(
        () => buildStatusFilterOptions(view.statusCounts, statusLabels, 'All'),
        [view.statusCounts, statusLabels]
    );
    const entityOptions = useMemo(() => buildEntityOptions(shownSlots, t('All')), [shownSlots, t]);

    const onFilterChange = useCallback((key, value) => {
        if (filters[key] === value) return;
        // Entity: only the level, never the picked entity.
        Events(`${EVENT_PREFIX}: filter changed`, {
            filter: key,
            value: key === 'entity' ? getEntityFilterLevel(value) : value,
        });
        setFilters((prev) => ({ ...prev, [key]: value }));
    }, [filters]);

    const identifier = resolveHeaderIdentifier({
        headerIdentifier: monthlyConfig.headerIdentifier,
        batteryInfo,
        vehicleInfo,
        fallbackRow: identifierFallbackRow,
        defaultLabel: t('common:device_id'),
    });

    // The CSV is the screen: same months, rows, filters and labels (statusLabels
    // translated like the tags), with the identifier as the first column.
    const buildCsvRows = useCallback(
        () => buildSohHistoryCsvRows({
            view,
            rowsConfig: monthlyConfig.rows,
            statusLabels: Object.fromEntries(Object.entries(statusLabels).map(([k, v]) => [k, tLabel(v)])),
            identifier: { label: identifier.label, value: identifier.value },
        }),
        [view, monthlyConfig.rows, statusLabels, tLabel, identifier.label, identifier.value]
    );

    // Client-side download: the pivot table writes these rows with DownloadCSV.
    const getCSVData = useCallback(async () => {
        Events(`${EVENT_PREFIX}: Download CSV`);
        const rows = buildCsvRows();
        if (!rows.length) {
            openNotification({ text: t('No SOH history available'), icon: 'toast-error' });
            return [];
        }
        return rows;
    }, [buildCsvRows, openNotification, t]);

    const getCSVFileName = useCallback(() => NameTheCSV('digitalTwin', CSV_COMPONENT_NAME), []);

    // Mail: the same rows go to the mail-only actions endpoint. The email
    // composer shows its own success toast once this resolves and logs when it
    // throws, so a failed send must throw.
    const onSendEmail = useCallback(
        async ({ sendTo, sendCc, subject, body }) => {
            Events(`${EVENT_PREFIX}: Send email`);
            let res = null;
            try {
                res = await sohHistoryAction(buildSohHistoryMailBody({
                    batteryID,
                    rows: buildCsvRows(),
                    emailTo: sendTo,
                    emailCc: sendCc,
                    subject,
                    body,
                    fileName: NameTheCSV('digitalTwin', CSV_COMPONENT_NAME),
                }));
            } catch (err) {
                res = null;
            }
            if (res?.responseStatus?.code !== CODE.SUCCESS) {
                openNotification({ text: t('Unable to send email'), icon: 'toast-error' });
                throw new Error(res?.responseStatus?.msg || 'SOH history email failed');
            }
        },
        [batteryID, buildCsvRows, openNotification, t]
    );

    const hasMonths = model.columns.length > 0;
    const summary = getMonthRangeSummary(view.months);
    const actionsEnabled = canRunSohHistoryActions({ hasMonths, isLoading });

    let emptyMessage = t('No SOH history available');
    if (hasError) emptyMessage = t('Unable to load SOH history');
    // Rows only alongside months: with no months (no data, or filters leave
    // none) the table shows its empty state instead of bare row labels.
    const showBody = hasMonths && !hasError;

    const renderCell = useCallback(
        (cell) => <SohPivotCell cell={cell} notComputedLabel={statusLabels.not_computed} />,
        [statusLabels.not_computed]
    );

    return (
        <div data-testid="soh-monthly-history">
            <MonthlyHistoricalPivotTable
                title={tLabel(monthlyConfig.monthlyHistoryLabel)}
                columns={showBody ? model.columns : []}
                rows={showBody ? model.rows : []}
                metricLabel={t('Metric')}
                renderCell={renderCell}
                isLoading={isLoading}
                emptyMessage={emptyMessage}
                filters={buildSohHistoryFilters({
                    t,
                    showStatus: monthlyConfig.filters.status,
                    showEntity: shouldShowEntityFilter(monthlyConfig.filters.entity, shownSlots),
                    showConfidence: monthlyConfig.filters.confidence,
                    statusOptions,
                    entityOptions,
                    filters,
                    onChange: onFilterChange,
                })}
                footer={
                    summary && !isLoading && !hasError ? (
                        <Typography variant="fw400s12c686868" data-testid="soh-history-summary" sx={{ display: 'block', mt: 1 }}>
                            {t('soh_history_summary', {
                                // Plural via i18next count; "between" because filtered months need not be contiguous.
                                defaultValue_one: 'Showing {{count}} month ({{start}})',
                                defaultValue_other: 'Showing {{count}} months between {{start}} and {{end}}',
                                count: summary.count,
                                start: summary.start,
                                end: summary.end,
                            })}
                        </Typography>
                    ) : null
                }
                disableDownloads={!monthlyConfig.actions.download}
                showEmailComposer={monthlyConfig.actions.email}
                actionsEnabled={actionsEnabled}
                getCSVData={getCSVData}
                getCSVFileName={getCSVFileName}
                onSendEmail={onSendEmail}
                availableEmailDomains={emailDomains}
                emailInfo={{ body: t('Please find attached the SOH monthly history.') }}
                testIdPrefix="soh-history"
                customRightActionsView={
                    <HeaderIdentifierBox
                        label={identifier.label}
                        value={identifier.value}
                        testId="battery-health-header-identifier-history"
                    />
                }
            />
        </div>
    );
};

export default SohMonthlyHistory;
